import React, { useRef, useState, useCallback } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

const EntryImageCard = ({ value, onChange, title = "Entry Image" }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result);
    reader.readAsDataURL(file);
  }, [onChange]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  }, [processFile]);

  return (
    <Card className="bg-gray-800 border-gray-700 mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-orange-500 text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-6">
          <div
            className={`w-32 h-32 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors flex-shrink-0 ${
              isDragging ? 'border-orange-500 bg-orange-500/10' : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="entry-image-dropzone"
          >
            {value ? (
              <img src={value} alt="Entry" className="w-full h-full object-cover rounded-lg" data-testid="entry-image-preview" />
            ) : (
              <div className="text-center p-2">
                <Camera className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                <span className="text-xs text-gray-400">{isDragging ? 'Drop image' : 'Drop or click'}</span>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                data-testid="entry-image-upload-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Photo
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                data-testid="entry-image-camera-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                Take Photo
              </Button>
              {value && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onChange('')}
                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                  data-testid="entry-image-remove"
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-400">Upload, drag & drop, or capture with camera</p>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => processFile(e.target.files?.[0])} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => processFile(e.target.files?.[0])} />
      </CardContent>
    </Card>
  );
};

export default EntryImageCard;
